import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { complexTableQuery } from "../common/utils/tableUtilities";
import { ensureInteger } from "../common/utils/generalUtils";
import { markFetched } from "../common/hooks/initHook";

const key = location => ensureInteger(location.id)
const orientationKey = orientation => ensureInteger(orientation.id);

export const locationSlice = createSlice({
    name: 'locations',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, l) => { obj[key(l)] = l; return obj; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(l => state.data[key(l)] = l);
        },
        addOrUpdateOrientation: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byLocation = groupBy(toUpdate, 'locationid');
            Object.entries(byLocation).forEach(([ locationid, orientations ]) => {
                const location = state.data[locationid];
                if (location) {
                    const ids = new Set(orientations.map(o => orientationKey(o)));
                    location.orientations = (location.orientations?.filter(o => !ids.has(orientationKey(o))) ?? []).concat(orientations);
                }
            });
        },
        removeOrientation: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byLocation = groupBy(toRemove, 'locationid');
            Object.entries(byLocation).forEach(([ locationid, orientations ]) => {
                const location = state.data[locationid];
                if (location) {
                    const ids = new Set(orientations.map(o => orientationKey(o)));
                    location.orientations = (location.orientations?.filter(o => !ids.has(orientationKey(o))) ?? []);
                }
            });
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(l => delete state.data[key(l)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => locationSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'location_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'location_one');

const fetchAllLocations = network => network.get('/api/v1/locations', locationSlice.actions.set, visualizeFailure('get'));

export const createLocation = (network, location) => network.post('/api/v1/location', location, visualizeSuccess('create', locationSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateLocation = (network, delta) => network.put('/api/v1/location', delta, locationSlice.actions.addOrUpdate, visualizeFailure('update'));
export const deleteLocation = (network, id) => network.delete('/api/v1/location/' + window.encodeURIComponent(id), visualizeSuccess('remove', locationSlice.actions.remove), visualizeFailure('remove'));

export const addProhibitedMember = (network, memberid, locationid) => network.post('/api/v1/memberProhibitedLocation', { memberid, locationid }, locationSlice.actions.addOrUpdate, DefaultFailureAction('create', 'prohibitedMembers'));
export const removeProhibitedMember = (network, memberid, locationid) => network.delete('/api/v1/memberProhibitedLocation/' + encodeURIComponent(memberid) + '/' + encodeURIComponent(locationid), locationSlice.actions.addOrUpdate, DefaultFailureAction('remove', 'prohibitedMembers'));
// #endregion

// #region selectors
const selectLocationData = state => state.locations.data;
const selectAllLocations = createSelector([selectLocationData], (locations) => Object.values(locations));
const selectLocation = id => createSelector([selectLocationData], locations => locations[id]);
const selectLocations = ids => createSelector([selectLocationData], locations => ids.reduce((res, id) => {
    if (locations[id]) {
        res.push(locations[id])
    }
    return res;
}, []));
const selectMemberProhibitedLocations = memberid => createSelector([selectLocationData], locations => Object.values(locations).filter(l => l.prohibited_members?.includes(memberid)));
const selectSearchLocations = search => createSelector([selectLocationData], locations => {
    let ignoreFields = Object.keys(search).filter(f => search[f] === undefined || search[f] === null || (Array.isArray(search[f]) && search[f].length === 0));
    let results = Object.values(locations)
    if (search.features) {
        ignoreFields.push('features');
        results = results.filter(location => {
            for (const feature of Object.keys(search.features)) {
                if (!!location.features?.[feature] !== !!search.features[feature]) {
                    return false;
                }
            }
            return true;
        })
    }
    return complexTableQuery(results, search, { ignoreFields });
})
// #endregion

const listen = {
    locationCreated: (location, { dispatch, websocket }) => {
        markFetched(websocket, 'location', key(location))
        dispatch(locationSlice.actions.addOrUpdate(location))
    },
    locationUpdated: (location, { dispatch }) => dispatch(locationSlice.actions.addOrUpdate(location)),
    locationRemoved: (location, { dispatch }) => dispatch(locationSlice.actions.remove(location)),
    locationOrientationCreated: (orientation, { dispatch }) => dispatch(locationSlice.actions.addOrUpdateOrientation(orientation)),
    locationOrientationUpdated: (orientation, { dispatch }) => dispatch(locationSlice.actions.addOrUpdateOrientation(orientation)),
    locationOrientationRemoved: (orientation, { dispatch }) => dispatch(locationSlice.actions.removeOrientation(orientation)),
}
const initLocations = { run: fetchAllLocations, listen, immortal: true };
export const [useAllLocations] = dataHookFactory(selectAllLocations, initLocations);
export const [useAllLocationsMap] = dataHookFactory(selectLocationData, initLocations);
export const [useLocations, realizeLocations] = dataHookFactory(selectLocations, ids => initLocations);
export const [useLocation, realizeLocation] = dataHookFactory(selectLocation, id => initLocations);
export const [useMemberProhbitedLocations] = dataHookFactory(selectMemberProhibitedLocations, memberid => initLocations);
export const [useLocationSearch] = dataHookFactory(selectSearchLocations, search => initLocations);
export const addressURL = (location) => {
    return location ? 'https://map.google.com.au?q=' + (location.address1 == null ? window.encodeURIComponent(location.name) : window.encodeURIComponent(location.address1) + (location.address2 ? ',%20' + window.encodeURIComponent(location.address2) : '') + ',%20' + window.encodeURIComponent(location.suburb) + '%20' + window.encodeURIComponent(location.state) + '%20' + window.encodeURIComponent(location.postcode)) : null;
}

const fullRoles = ['administrator', 'event manager', 'template manager'];
const readRoles = ['auditor', 'event auditor','template auditor','rostering manager','rostering auditor','recurring rostering manager','recurring rostering auditor','timesheet manager','timesheet auditor', 'member auditor', 'member manager','accreditation auditor'];
export const canUpdateLocation = (credentials, offices) => checkPermissions(credentials, fullRoles, offices);
export const canCreateLocation = (credentials) => checkPermissions(credentials, fullRoles);
export const canRemoveLocation = canUpdateLocation;
export const canViewLocation = (credentials) => checkPermissions(credentials, fullRoles.concat(readRoles));
export const canRouteLocations = ({credentials}) => canViewLocation(credentials);

const checkPermissions = (credentials, requiredRoles, offices) => {
    return credentials.hasRole(requiredRoles, {office: offices});
}
    

const reducer = { name: locationSlice.name, reducer: locationSlice.reducer }
export default reducer;